// packages/i18n/src/index.ts
//
// Per CONTEXT.md. Covers G-NOTIF-02.
// Barrel: notification key registry + locale lookup helper.
//
// Phase 4.12 Task 09 (P4.12.D-16/D-17/D-22) — vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape
// Adds a SEPARATE error-message catalog (`lookupError` /
// `assertErrorCatalogComplete`) alongside the existing notification catalog.
// The error catalog resolves to a single localized STRING per key (not the
// notification catalog's {title, body} pair) — see error-keys.ts. `lookup()`
// and `assertCatalogComplete()` below are UNCHANGED; notification producers
// across the API depend on their existing contract.
import enNotifications from '../locales/en/notifications.json' with { type: 'json' };
import viNotifications from '../locales/vi/notifications.json' with { type: 'json' };
import frNotifications from '../locales/fr/notifications.json' with { type: 'json' };
import enErrors from '../locales/en/errors.json' with { type: 'json' };
import viErrors from '../locales/vi/errors.json' with { type: 'json' };
import frErrors from '../locales/fr/errors.json' with { type: 'json' };
import { FALLBACK_LOCALE, NOTIFICATION_KEYS, NOTIFICATION_KEY_LIST, SUPPORTED_LOCALES, isNotificationKey, } from './notifications-keys.js';
import { ERROR_KEYS, ERROR_KEY_LIST, isErrorKey, } from './error-keys.js';
export { FALLBACK_LOCALE, NOTIFICATION_KEYS, NOTIFICATION_KEY_LIST, SUPPORTED_LOCALES, isNotificationKey, ERROR_KEYS, ERROR_KEY_LIST, isErrorKey, };
const CATALOGS = {
    en: enNotifications,
    vi: viNotifications,
    fr: frNotifications,
};
/**
 * Substitute `{{name}}` placeholders with values from `params`.
 * Missing placeholders are left as-is so caller can detect via tests.
 */
function interpolate(template, params) {
    if (!params)
        return template;
    return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (match, key) => {
        const value = params[key];
        return value === undefined || value === null ? match : String(value);
    });
}
/**
 * Resolve a notification key into a localized {title, body} pair.
 *
 * Order: `lang` catalog → `fallback` catalog (default 'en') → throws.
 * Per Task 15a +: dispatcher uses this server-side; client may
 * also call it for in-app feed rendering.
 */
export function lookup(key, lang, params, options) {
    if (!isNotificationKey(key)) {
        throw new Error(`[i18n] Unknown notification key: ${key}`);
    }
    const fallback = options?.fallback ?? FALLBACK_LOCALE;
    const primary = CATALOGS[lang]?.[key];
    const resolved = primary ??
        (fallback !== 'none' ? CATALOGS[fallback]?.[key] : undefined);
    if (!resolved) {
        throw new Error(`[i18n] Missing translation for ${key} in ${lang} (fallback=${fallback})`);
    }
    return {
        title: interpolate(resolved.title, params),
        body: interpolate(resolved.body, params),
    };
}
/**
 * Bulk-validate that every key in the registry has an entry in every locale.
 * Used by tests + CI to prevent shipping incomplete translations.
 */
export function assertCatalogComplete() {
    const missing = [];
    for (const locale of SUPPORTED_LOCALES) {
        const catalog = CATALOGS[locale];
        for (const key of NOTIFICATION_KEY_LIST) {
            if (!catalog?.[key]) {
                missing.push(`${locale}:${key}`);
            }
        }
    }
    if (missing.length > 0) {
        throw new Error(`[i18n] Notification catalog incomplete:\n  - ${missing.join('\n  - ')}`);
    }
}
const ERROR_CATALOGS = {
    en: enErrors,
    vi: viErrors,
    fr: frErrors,
};
/**
 * Resolve an error key into a localized string (single string, NOT the
 * notification catalog's {title, body} pair).
 *
 * Order: `lang` catalog -> `fallback` catalog (default 'en') -> throws.
 * Used by `apps/api/src/modules/orders/errors/quote-stale.error.ts` builders
 * to derive `error.message` — `i18n_key` on the wire envelope stays
 * authoritative for the client; this helper is for any server-side literal
 * fallback rendering (e.g. logs, non-i18n-aware legacy consumers).
 */
export function lookupError(key, lang, params, options) {
    if (!isErrorKey(key)) {
        throw new Error(`[i18n] Unknown error key: ${key}`);
    }
    const fallback = options?.fallback ?? FALLBACK_LOCALE;
    const primary = ERROR_CATALOGS[lang]?.[key];
    const resolved = primary ??
        (fallback !== 'none' ? ERROR_CATALOGS[fallback]?.[key] : undefined);
    if (!resolved) {
        throw new Error(`[i18n] Missing error translation for ${key} in ${lang} (fallback=${fallback})`);
    }
    return interpolate(resolved, params);
}
/**
 * Sibling of `assertCatalogComplete()` — bulk-validates the error catalog
 * (equal key counts across en/vi/fr, P4.12.D-22). Kept separate so extending
 * one catalog can never silently regress the other's assertion coverage.
 */
export function assertErrorCatalogComplete() {
    const missing = [];
    for (const locale of SUPPORTED_LOCALES) {
        const catalog = ERROR_CATALOGS[locale];
        for (const key of ERROR_KEY_LIST) {
            if (!catalog?.[key]) {
                missing.push(`${locale}:${key}`);
            }
        }
    }
    if (missing.length > 0) {
        throw new Error(`[i18n] Error catalog incomplete:\n  - ${missing.join('\n  - ')}`);
    }
}
//# sourceMappingURL=index.js.map